import { call, put, takeEvery, takeLatest } from 'redux-saga/effects';
import callApi, { abortableCallApi } from 'utils/callApi';
import { LOCATION_CHANGE } from 'react-router-redux';

import { requestCreateBusinessDocument } from 'redux/modules/business';
import { fetchAccountInformationSuccess } from 'redux/modules/account';
import { fetchAppInfoSuccess } from 'redux/modules/appInfo';
import { addNotification } from 'redux/modules/notifications';
import { saveRecoverOrder } from 'redux/modules/recoverOrder';
import { requestBulk } from 'redux/modules/bulk';

// Actions
export const FETCH_FACILITIES = 'FETCH_FACILITIES';
export const FETCH_FACILITIES_SUCCESS = 'FETCH_FACILITIES_SUCCESS';
export const FETCH_FACILITIES_FAIL = 'FETCH_FACILITIES_FAIL';
export const FETCH_FACILITY = 'FETCH_FACILITY';
export const FETCH_FACILITY_SUCCESS = 'FETCH_FACILITY_SUCCESS';
export const FETCH_FACILITY_FAIL = 'FETCH_FACILITY_FAIL';
export const CREATE_FACILITY = 'CREATE_FACILITY';
export const CREATE_FACILITY_SUCCESS = 'CREATE_FACILITY_SUCCESS';
export const CREATE_FACILITY_FAIL = 'CREATE_FACILITY_FAIL';
export const UPDATE_FACILITY = 'UPDATE_FACILITY';
export const UPDATE_FACILITY_SUCCESS = 'UPDATE_FACILITY_SUCCESS';
export const UPDATE_FACILITY_FAIL = 'UPDATE_FACILITY_FAIL';
export const DELETE_FACILITY = 'DELETE_FACILITY';
export const DELETE_FACILITY_SUCCESS = 'DELETE_FACILITY_SUCCESS';
export const DELETE_FACILITY_FAIL = 'DELETE_FACILITY_FAIL';

// Reducer
export default function facilitiesReducer(state = { data: [], count: 0, loading: 0 }, action) {
  const { type } = action;
  let triggerLoading;
  let payload;
  if (action.payload) {
    ({ triggerLoading, ...payload } = action.payload);
  }
  switch (type) {
    case FETCH_FACILITIES:
      return {
        ...state,
        ...triggerLoading ? { loading: state.loading + 1 } : {},
      };
    case FETCH_FACILITIES_SUCCESS:
      if (payload.push) {
        const newData = [
          ...state.data.slice(0, state.data.length),
          ...payload.data.slice(0, payload.data.length),
        ];
        return {
          ...state,
          count: payload.count,
          data: newData,
          ...triggerLoading ? { loading: state.loading - 1 } : {},
        };
      }
      return {
        ...state,
        ...payload,
        ...triggerLoading ? { loading: state.loading - 1 } : {},
      };
    case FETCH_FACILITIES_FAIL:
      return {
        ...state,
        ...triggerLoading ? { loading: state.loading - 1 } : {},
      };
    case FETCH_FACILITY:
      return {
        ...state,
        ...triggerLoading ? { loading: state.loading + 1 } : {},
      };
    case FETCH_FACILITY_SUCCESS:
    case CREATE_FACILITY_SUCCESS: {
      const newData = [
        ...state.data.slice(0, state.data.length),
        payload,
      ];
      return {
        ...state,
        count: state.count + 1,
        data: newData,
        ...triggerLoading ? { loading: state.loading - 1 } : {},
      };
    }
    case FETCH_FACILITY_FAIL:
      return {
        ...state,
        ...triggerLoading ? { loading: state.loading - 1 } : {},
      };
    case UPDATE_FACILITY_SUCCESS: {
      const newData = state.data.map((dataItem) => {
        if (dataItem.ID !== payload.ID) {
          return dataItem;
        }
        return {
          ...dataItem,
          ...payload,
        };
      });
      return {
        ...state,
        data: newData,
      };
    }
    case DELETE_FACILITY:
      return {
        ...state,
        ...triggerLoading ? { loading: state.loading + 1 } : {},
      };
    case DELETE_FACILITY_SUCCESS: {
      const index = state.data.findIndex(dataItem => dataItem.ID === payload.ID);
      const newData = [
        ...state.data.slice(0, index),
        ...state.data.slice(index + 1),
      ];
      return {
        ...state,
        count: state.count - 1,
        data: newData,
        ...triggerLoading ? { loading: state.loading - 1 } : {},
      };
    }
    case DELETE_FACILITY_FAIL:
      return {
        ...state,
        ...triggerLoading ? { loading: state.loading - 1 } : {},
      };
    case LOCATION_CHANGE:
      return {
        ...state,
        loading: 0,
      };
    default:
      return state;
  }
}

// Action Creators
export function fetchFacilities(payload) {
  return { type: FETCH_FACILITIES, payload };
}

export function fetchFacilitiesSuccess(payload) {
  return { type: FETCH_FACILITIES_SUCCESS, payload };
}

export function fetchFacilitiesFail(payload) {
  return { type: FETCH_FACILITIES_FAIL, payload };
}

export function fetchFacility(payload) {
  return { type: FETCH_FACILITY, payload };
}

export function fetchFacilitySuccess(payload) {
  return { type: FETCH_FACILITY_SUCCESS, payload };
}

export function fetchFacilityFail(payload) {
  return { type: FETCH_FACILITY_FAIL, payload };
}

export function createFacility(payload) {
  return { type: CREATE_FACILITY, payload };
}

export function createFacilitySuccess(payload) {
  return { type: CREATE_FACILITY_SUCCESS, payload };
}

export function createFacilityFail(payload) {
  return { type: CREATE_FACILITY_FAIL, payload };
}

export function updateFacility(payload) {
  return { type: UPDATE_FACILITY, payload };
}

export function updateFacilitySuccess(payload) {
  return { type: UPDATE_FACILITY_SUCCESS, payload };
}

export function updateFacilityFail(payload) {
  return { type: UPDATE_FACILITY_FAIL, payload };
}

export function deleteFacility(payload) {
  return { type: DELETE_FACILITY, payload };
}

export function deleteFacilitySuccess(payload) {
  return { type: DELETE_FACILITY_SUCCESS, payload };
}

export function deleteFacilityFail(payload) {
  return { type: DELETE_FACILITY_FAIL, payload };
}

// Requests

let abort;

export function requestFetchFacilities({ limit, offset }) {
  if (abort) {
    abort();
    abort = null;
  }
  const request = abortableCallApi(`settings/facility/?limit=${limit}&offset=${offset}`);
  abort = request.abort;
  return request.res;
}

export function requestSearchFacilities({ limit, offset, filters = [] }) {
  if (abort) {
    abort();
    abort = null;
  }
  const request = abortableCallApi('settings/facility/search/', 'POST', {
    filters,
    sort_by: [
      {
        name: 'ID',
        sort: 'asc',
      },
    ],
    limit,
    offset,
  });
  abort = request.abort;
  return request.res;
}

export function requestFetchFacility(payload) {
  return callApi(`settings/facility/?id=${payload}`);
}

export function requestCreateFacility(payload) {
  return callApi('settings/facility/', 'POST', payload);
}

export function requestUpdateFacility(payload) {
  return callApi('settings/facility/', 'PATCH', payload);
}

export function requestDeleteFacility(payload) {
  return callApi(`settings/facility/?id=${payload}`, 'DELETE');
}

// Sagas
function* fetchFacilitiesSaga(action) {
  const { triggerLoading, recoverOrderValues } = action.payload;
  try {
    const res = action.payload.filters
      ? yield call(requestSearchFacilities, action.payload)
      : yield call(requestFetchFacilities, action.payload);
    if (res.count === 0) {
      yield put(fetchFacilitiesSuccess({
        data: res.data,
        count: res.count,
        triggerLoading,
      }));
    } else if (action.payload.push) {
      yield put(fetchFacilitiesSuccess({
        push: true,
        data: res.data,
        count: res.count,
        triggerLoading,
      }));
    } else {
      yield put(fetchFacilitiesSuccess({
        data: res.data,
        count: res.count,
        triggerLoading,
      }));
    }
    yield put(fetchAccountInformationSuccess(res.user_info));
    yield put(fetchAppInfoSuccess(res.app_info));
  } catch (err) {
    if (err.data.error === 'Need authorisation' && recoverOrderValues) {
      yield put(saveRecoverOrder(recoverOrderValues));
    }
    yield put(fetchFacilitiesFail({
      ...err.data,
      triggerLoading,
    }));
  }
}

function* fetchFacilitySaga(action) {
  const { triggerLoading } = action.payload;
  try {
    const res = yield call(requestFetchFacility, action.payload.ID);
    if (res.data.ID) {
      yield put(fetchFacilitySuccess({
        ...res.data,
        triggerLoading,
      }));
    } else {
      yield put(fetchFacilityFail({
        triggerLoading,
      }));
    }
    yield put(fetchAccountInformationSuccess(res.user_info));
    yield put(fetchAppInfoSuccess(res.app_info));
  } catch (err) {
    yield put(fetchFacilityFail({
      ...err.data,
      triggerLoading,
    }));
  }
}

function* createFacilitySaga(action) {
  const {
    values: {
      Image,
      ...otherValues
    },
    resolve,
    reject,
  } = action.payload;
  try {
    let imageRes; 
    const res = yield call(requestCreateFacility, {
      'Company ID': 1,
      'Work Schedule ID': otherValues['Work Schedule ID'],     
    });
    if (Image) {
      const formData = new FormData();
      formData.append('object_id', res.data.ID);
      if (Image.noFileConstructor) {
        delete Image.noFileConstructor;
        formData.append('file', Image, Image.name);
      } else {
        formData.append('file', Image);
      }
      imageRes = yield call(requestCreateBusinessDocument, formData);
    }
    const bulkRes = yield call(requestBulk, {
      ...imageRes && imageRes.data.ID ? { 'Company': {
        URL: 'settings/facility/',
        METHOD: 'PATCH',
        BODY: {
          ID: res.data.ID,
          'Image ID': imageRes.data.ID,
        },
      } } : {},
      ...otherValues['Company Info'] ? { 'Company Info': {
        URL: 'settings/company_info/',
        METHOD: 'POST',
        BODY: {
          'Facility ID': res.data.ID,
          ...otherValues['Company Info'],
        },
      } } : {},
      ...otherValues['Company Contact'] ? { 'Company Contact': {
        URL: 'settings/company_contact/',
        METHOD: 'POST',
        BODY: {
          'Facility ID': res.data.ID,
          ...otherValues['Company Contact'],
        },
      } } : {},
      ...otherValues['Company Address'] ? { 'Company Address': {
        URL: 'settings/company_address/',
        METHOD: 'POST',
        BODY: {
          'Facility ID': res.data.ID,
          ...otherValues['Company Address'],
        },
      } } : {},
      ...otherValues['Company Identify'] ? { 'Company Identify': {
        URL: 'settings/company_identify/',
        METHOD: 'POST',
        BODY: {
          'Facility ID': res.data.ID,
          ...otherValues['Company Identify'],
        },
      } } : {},
    });
    const facilityRes = {
      ...res.data.ID ? { ...res.data } : {},
      ...bulkRes.data.Company ? { ...bulkRes.data.Company.data } : {},
      ...bulkRes.data['Company Info'] ? {
        'Company Info': bulkRes.data['Company Info'].data,
      } : {},
      ...bulkRes.data['Company Contact'] ? {
        'Company Contact': bulkRes.data['Company Contact'].data,
      } : {},
      ...bulkRes.data['Company Address'] ? {
        'Company Address': bulkRes.data['Company Address'].data,
      } : {},
      ...bulkRes.data['Company Identify'] ? {
        'Company Identify': bulkRes.data['Company Identify'].data,
      } : {},
    };
    yield put(createFacilitySuccess(facilityRes));
    yield put(fetchAccountInformationSuccess(res.user_info));
    yield put(fetchAppInfoSuccess(res.app_info));
    yield put(addNotification({
      message: 'New facility successfully added',
      variant: 'success',
    }));
    if (resolve) {
      resolve(facilityRes);
    }
  } catch (err) {
    yield put(createFacilityFail(err.data));
    if (reject) {
      reject(err.data);
    }
  }
}

function* updateFacilitySaga(action) {
  const {
    values: {
      Image,
      ...otherValues
    },
    resolve,
    reject,
  } = action.payload;
  try {
    let imageRes;
    if (Image) {
      const formData = new FormData();
      formData.append('object_id', otherValues.ID);
      if (Image.noFileConstructor) {
        delete Image.noFileConstructor;
        formData.append('file', Image, Image.name);
      } else {
        formData.append('file', Image);
      }
      imageRes = yield call(requestCreateBusinessDocument, formData);
    }
    const res = yield call(requestBulk, {
      ...(otherValues['Work Schedule ID'] || Image === null || (imageRes && imageRes.data.ID)) ? { 'Facility': {
        URL: 'settings/facility/',
        METHOD: 'PATCH',
        BODY: {
          ID: otherValues.ID,
          'Work Schedule ID': otherValues['Work Schedule ID'],
          ...Image === null ? {
            'Image ID': null,
          } : {},
          ...imageRes && imageRes.data.ID ? {
            'Image ID': imageRes.data.ID,
          } : {},
        },
      } } : {},
      ...otherValues['Company Info'] ? { 'Company Info': {
        URL: 'settings/company_info/',
        METHOD: otherValues['Company Info'].ID ? 'PATCH' : 'POST',
        BODY: otherValues['Company Info'],
      } } : {},
      ...otherValues['Company Contact'] ? { 'Company Contact': {
        URL: 'settings/company_contact/',
        METHOD: otherValues['Company Contact'].ID ? 'PATCH' : 'POST',
        BODY: otherValues['Company Contact'],
      } } : {},
      ...otherValues['Company Address'] ? { 'Company Address': {
        URL: 'settings/company_address/',
        METHOD: otherValues['Company Address'].ID ? 'PATCH' : 'POST',
        BODY: otherValues['Company Address'],
      } } : {},
      ...otherValues['Company Identify'] ? { 'Company Identify': {
        URL: 'settings/company_identify/',
        METHOD: otherValues['Company Identify'].ID ? 'PATCH' : 'POST',
        BODY: otherValues['Company Identify'],
      } } : {},
    });
    const facilityRes = {
      ...res.data.Facility ? { ...res.data.Facility.data } : {},
      ...res.data['Company Info'] ? {
        'Company Info': res.data['Company Info'].data,
      } : {},
      ...res.data['Company Contact'] ? {
        'Company Contact': res.data['Company Contact'].data,
      } : {},
      ...res.data['Company Address'] ? {
        'Company Address': res.data['Company Address'].data,
      } : {},
      ...res.data['Company Identify'] ? {
        'Company Identify': res.data['Company Identify'].data,
      } : {},
    };
    yield put(updateFacilitySuccess(facilityRes));
    yield put(fetchAccountInformationSuccess(res.user_info));
    yield put(fetchAppInfoSuccess(res.app_info));
    yield put(addNotification({
      message: 'Facility successfully updated',
      variant: 'success',
    }));
    if (resolve) {
      resolve(facilityRes);
    }
  } catch (err) {
    yield put(updateFacilityFail(err.data));
    if (reject) {
      reject(err.data);
    }
  }
}

function* deleteFacilitySaga(action) {
  const { triggerLoading, ID } = action.payload;
  try {
    const res = yield call(requestDeleteFacility, ID);
    yield put(deleteFacilitySuccess({
      ID,
      triggerLoading,
    }));
    yield put(fetchAccountInformationSuccess(res.user_info));
    yield put(fetchAppInfoSuccess(res.app_info));
    yield put(addNotification({
      message: 'Facility successfully deleted',
      variant: 'success',
    }));
  } catch (err) {
    yield put(deleteFacilityFail({
      ...err.data,
      triggerLoading,
    }));
    yield put(addNotification({
      message: 'Failed to delete facility',
      variant: 'warning',
    }));
  }
}

export function* facilitiesSaga() {
  yield takeLatest(FETCH_FACILITIES, fetchFacilitiesSaga);
  yield takeLatest(FETCH_FACILITY, fetchFacilitySaga);
  yield takeEvery(CREATE_FACILITY, createFacilitySaga);
  yield takeEvery(UPDATE_FACILITY, updateFacilitySaga);
  yield takeEvery(DELETE_FACILITY, deleteFacilitySaga);
}



// WEBPACK FOOTER //
// ./src/redux/modules/facilities.js